import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import {
  deriveAgentPanelModel,
  foldSubagentActivities,
} from "@t3tools/client-runtime/state/subagentRuntime";
import { useMemo } from "react";

import { type AppState, selectProjectByRef, useStore } from "../../store";
import { createThreadSelectorByRef } from "../../storeSelectors";
import { AgentsFleetPanel } from "../agents/AgentsFleetPanel";
import { resolveDiffCheckoutPath } from "../diffTargetResolution";

const EMPTY_ACTIVITY_BY_ID: Record<string, never> = {};
import { FilePreviewPanel } from "../files/FilePreviewPanel";
import { SimulatorPanel } from "../simulator/SimulatorPanel";
import { useSimulatorLease } from "../simulator/useSimulatorLease";
import { ChatTaskPanel } from "../tasks/ChatTaskPanel";
import type { RightPanelSurface } from "../../rightPanelSurface";

export function SimulatorPane({ threadRef }: { readonly threadRef: ScopedThreadRef }) {
  const lease = useSimulatorLease({
    forgeEnvironmentId: threadRef.environmentId,
    threadId: threadRef.threadId,
  });

  return (
    <SimulatorPanel
      state={lease.state}
      deviceWidthPt={402}
      deviceHeightPt={874}
      lastFrameAtMs={null}
      viewportWidthPx={typeof window === "undefined" ? 1280 : window.innerWidth}
      onTakeControl={lease.state === "live" ? lease.onRelease : lease.onConsent}
      onRetry={lease.onRetry}
    />
  );
}

export function ChatTaskPane({ threadRef }: { readonly threadRef: ScopedThreadRef }) {
  const thread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));
  return (
    <ChatTaskPanel
      anchor={{
        anchorKind: thread?.anchorKind ?? null,
        anchorCmmdId: thread?.anchorCmmdId ?? null,
      }}
    />
  );
}

/**
 * The fleet view over this thread's subagent fold (ticket 3531). Activities
 * come from the per-environment detail stream; a dead session derives
 * interruption exactly like upstream's derivePhase (any session that is not
 * disconnected, closed, or errored keeps agents live).
 */
export function ChatAgentsPane({ threadRef }: { readonly threadRef: ScopedThreadRef }) {
  const activitiesById = useStore(
    useMemo(
      () => (state: AppState) =>
        state.environmentStateById[threadRef.environmentId]?.activityByThreadId[
          threadRef.threadId
        ] ?? EMPTY_ACTIVITY_BY_ID,
      [threadRef],
    ),
  );
  const sessionLive = useStore(
    useMemo(() => {
      const selector = (state: AppState): boolean => {
        const status =
          state.environmentStateById[threadRef.environmentId]?.threadSessionById[threadRef.threadId]
            ?.status;
        return status === "connecting" || status === "ready" || status === "running";
      };
      return selector;
    }, [threadRef]),
  );
  const model = useMemo(
    () =>
      deriveAgentPanelModel({
        agents: foldSubagentActivities(Object.values(activitiesById), { sessionLive }),
      }),
    [activitiesById, sessionLive],
  );

  return (
    <AgentsFleetPanel
      model={model}
      environmentId={threadRef.environmentId}
      threadId={threadRef.threadId}
    />
  );
}

function useThreadCheckout(threadRef: ScopedThreadRef): {
  readonly cwd: string;
  readonly cmmdProjectId: number | null;
} | null {
  const thread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));
  const project = useStore((store) =>
    thread?.environmentId && thread?.projectId
      ? selectProjectByRef(store, {
          environmentId: thread.environmentId,
          projectId: thread.projectId,
        })
      : undefined,
  );
  const cwd = resolveDiffCheckoutPath({
    threadWorktreePath: thread?.worktreePath,
    projectCwd: project?.cwd,
  });
  return cwd ? { cwd, cmmdProjectId: project?.cmmdRegistration?.cmmdProjectId ?? null } : null;
}

export function ThreadFilePane({
  surface,
  threadRef,
}: {
  readonly surface: Extract<RightPanelSurface, { kind: "file" }>;
  readonly threadRef: ScopedThreadRef;
}) {
  const checkout = useThreadCheckout(threadRef);
  if (!checkout) {
    return (
      <div className="flex flex-1 items-center justify-center px-5 text-center text-xs text-muted-foreground/70">
        This thread has no checkout to read files from yet.
      </div>
    );
  }

  return (
    <FilePreviewPanel
      environmentId={threadRef.environmentId}
      projectId={checkout.cmmdProjectId}
      cwd={checkout.cwd}
      relativePath={surface.relativePath}
      revealLine={surface.revealLine}
      revealRequestId={surface.revealRequestId}
      readPolicy={surface.readPolicy}
    />
  );
}
